import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Card, CardContent, Chip, Typography, TextField, Button, Stack, CircularProgress, Alert, Link,
} from "@mui/material";
import { listDrafts, approveDraft, rejectDraft } from "../api";
import { routingColor } from "../theme";
import { useAuth } from "../auth/AuthContext";

function ExistingReplies({ replies }) {
  if (!replies || replies.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {replies.length} existing repl{replies.length === 1 ? "y" : "ies"} on this comment:
      </Typography>
      {replies.map((r, i) => (
        <Box key={i} sx={{
          mt: 0.5, pl: 1.5, py: 0.5, fontSize: 13, whiteSpace: "pre-wrap",
          borderLeft: "3px solid", borderColor: r.from_page ? "primary.main" : "#e0e0e0",
          bgcolor: r.from_page ? "#FFF7EF" : "transparent",
        }}>
          <strong>{r.from_page ? "Freedom Power" : (r.from_name || "Someone")}:</strong> {r.message}
        </Box>
      ))}
    </Box>
  );
}

export default function ReviewQueue() {
  const { user } = useAuth();
  const canWrite = !!user?.can_write;
  const [drafts, setDrafts] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState(null);
  const focusId = new URLSearchParams(window.location.search).get("id");
  const refs = useRef({});

  const load = useCallback(async () => {
    try { setDrafts(await listDrafts()); } catch { setErr("Could not load the queue."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (drafts && focusId && refs.current[focusId]) {
      refs.current[focusId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [drafts, focusId]);

  const remove = (id) => setDrafts((d) => d.filter((x) => x.id !== id));

  const onApprove = async (d) => {
    if (d.already_replied &&
        !window.confirm("The Page has already replied to this comment. Post another reply anyway?")) return;
    setBusy((b) => ({ ...b, [d.id]: true }));
    try {
      const text = edits[d.id] ?? d.draft_reply;
      let res;
      try {
        res = await approveDraft(d.id, text);
      } catch (e) {
        if (e?.response?.status === 409 && e.response.data?.already_replied) {
          if (!window.confirm("Heads up — the Page already has a reply on this comment. Post anyway?")) {
            setBusy((b) => ({ ...b, [d.id]: false }));
            return;
          }
          res = await approveDraft(d.id, text, true);
        } else { throw e; }
      }
      if (res?.success) remove(d.id); else alert(res?.error || "Failed");
    } catch (e) { alert(e?.response?.data?.error || "Failed to post reply"); }
    finally { setBusy((b) => ({ ...b, [d.id]: false })); }
  };

  const onReject = async (d) => {
    if (!window.confirm("Reject this draft? No reply will be posted.")) return;
    setBusy((b) => ({ ...b, [d.id]: true }));
    try { const res = await rejectDraft(d.id); if (res.success) remove(d.id); }
    finally { setBusy((b) => ({ ...b, [d.id]: false })); }
  };

  if (err) return <Alert severity="error">{err}</Alert>;
  if (drafts === null) return <Box sx={{ display: "grid", placeItems: "center", py: 8 }}><CircularProgress /></Box>;
  if (drafts.length === 0) return <Typography sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>🎉 Nothing to review.</Typography>;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{drafts.length} awaiting review</Typography>
      <Stack spacing={2}>
        {drafts.map((d) => {
          const rc = routingColor[d.routing] || routingColor.needs_decision;
          const highlight = String(d.id) === String(focusId);
          return (
            <Card key={d.id} ref={(el) => { refs.current[d.id] = el; }}
                  sx={{ outline: highlight ? "3px solid" : "none", outlineColor: "primary.main" }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 1 }}>
                  <Chip size="small" label={rc.label} sx={{ bgcolor: rc.bg, color: rc.text, fontWeight: 600 }} />
                  <Chip size="small" variant="outlined" label={d.category} />
                  {d.is_likely_customer && <Chip size="small" variant="outlined" label="likely customer" />}
                  {d.already_replied && <Chip size="small" color="error" label="⚠ Page already replied" />}
                  <Typography variant="caption" color="text.secondary">confidence: {d.confidence}</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {d.permalink && <Link href={d.permalink} target="_blank" rel="noopener" variant="caption">view on Facebook ↗</Link>}
                </Stack>
                <Typography variant="subtitle2">{d.author_name || "Someone"}</Typography>
                <Box sx={{ bgcolor: "#f7f8fa", borderLeft: "3px solid #cdd2d8", borderRadius: 1, px: 1.5, py: 1, my: 1, whiteSpace: "pre-wrap" }}>
                  {d.comment_text}
                </Box>

                <ExistingReplies replies={d.existing_replies} />

                <TextField fullWidth multiline minRows={2} sx={{ mt: 1.5 }}
                  value={edits[d.id] ?? d.draft_reply ?? ""}
                  onChange={(e) => setEdits((s) => ({ ...s, [d.id]: e.target.value }))}
                  disabled={!canWrite} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
                  Claude: {d.reason}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button variant="contained" color={d.already_replied ? "warning" : "primary"}
                    disabled={!canWrite || busy[d.id]} onClick={() => onApprove(d)}>
                    {d.already_replied ? "Approve anyway" : "Approve & post"}
                  </Button>
                  <Button color="error" variant="outlined" disabled={!canWrite || busy[d.id]} onClick={() => onReject(d)}>Reject</Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
